import { ChevronDown, ChevronRight, Copy, Eye, EyeOff } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

interface LazyJsonPreviewProps {
	/** The data to display as JSON */
	data: any;
	/** Maximum number of items to show initially */
	maxItems?: number;
	/** Maximum depth to expand objects/arrays */
	maxDepth?: number;
	/** Whether to show the preview by default */
	defaultVisible?: boolean;
	/** Custom label for the preview section */
	label?: string;
	/** Additional CSS classes */
	className?: string;
}

interface JsonNodeProps {
	data: any;
	depth: number;
	maxDepth: number;
	keyName?: string;
	isLast?: boolean;
}

/**
 * A performance-optimized JSON preview component that handles large datasets
 * by truncating content, providing pagination, and lazy-rendering tree structures.
 *
 * Features:
 * - Truncates large arrays/objects to prevent DOM explosion
 * - Collapsible tree view for nested structures
 * - Copy-to-clipboard functionality
 * - Toggle visibility to save memory
 * - Respects max depth to prevent infinite nesting issues
 */
export function LazyJsonPreview({
	data,
	maxItems = 20,
	maxDepth = 3,
	defaultVisible = true,
	label = 'Preview Data',
	className = '',
}: LazyJsonPreviewProps) {
	const [isVisible, setIsVisible] = useState(defaultVisible);
	const [currentPage, setCurrentPage] = useState(0);
	const itemsPerPage = maxItems;

	// Memoize the processed data to avoid expensive computations on every render
	const processedData = useMemo(() => {
		if (!data) return null;

		// For arrays, implement pagination
		if (Array.isArray(data)) {
			const startIndex = currentPage * itemsPerPage;
			const endIndex = startIndex + itemsPerPage;
			const paginatedData = data.slice(startIndex, endIndex);

			return {
				type: 'array',
				data: paginatedData,
				totalItems: data.length,
				hasMore: data.length > endIndex,
				currentPage,
				totalPages: Math.ceil(data.length / itemsPerPage),
			};
		}

		// For objects, truncate properties if too many
		if (typeof data === 'object' && data !== null) {
			const entries = Object.entries(data);
			if (entries.length > maxItems) {
				const truncatedEntries = entries.slice(0, maxItems);
				const truncatedData = Object.fromEntries(truncatedEntries);

				return {
					type: 'object',
					data: truncatedData,
					totalItems: entries.length,
					truncated: true,
				};
			}
		}

		return {
			type: 'primitive',
			data,
			totalItems: 1,
		};
	}, [data, currentPage, itemsPerPage, maxItems]);

	const handleCopyToClipboard = useCallback(() => {
		if (data) {
			navigator.clipboard.writeText(JSON.stringify(data, null, 2));
		}
	}, [data]);

	const handleNextPage = useCallback(() => {
		if (processedData?.type === 'array' && processedData.hasMore) {
			setCurrentPage(prev => prev + 1);
		}
	}, [processedData]);

	const handlePreviousPage = useCallback(() => {
		if (currentPage > 0) {
			setCurrentPage(prev => prev - 1);
		}
	}, [currentPage]);

	if (!data) {
		return (
			<div
				className={`p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700 ${className}`}
			>
				<div className="text-sm text-gray-500 dark:text-gray-400 text-center">
					No preview data available
				</div>
			</div>
		);
	}

	return (
		<div
			className={`bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700 ${className}`}
		>
			{/* Header */}
			<div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
				<button
					onClick={() => setIsVisible(!isVisible)}
					className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
				>
					{isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
					{label}
					{processedData && (
						<span className="text-xs text-gray-500 dark:text-gray-400">
							({processedData.totalItems}{' '}
							{processedData.totalItems === 1 ? 'item' : 'items'})
						</span>
					)}
				</button>

				<button
					onClick={handleCopyToClipboard}
					className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
					title="Copy to clipboard"
				>
					<Copy className="w-4 h-4" />
				</button>
			</div>

			{/* Content */}
			{isVisible && (
				<div className="p-3">
					{processedData?.type === 'array' && (
						<>
							{/* Pagination info */}
							<div className="flex items-center justify-between mb-3 text-xs text-gray-600 dark:text-gray-400">
								<span>
									Showing {currentPage * itemsPerPage + 1}-
									{Math.min(
										(currentPage + 1) * itemsPerPage,
										processedData.totalItems
									)}{' '}
									of {processedData.totalItems}
								</span>
								<div className="flex items-center gap-2">
									<button
										onClick={handlePreviousPage}
										disabled={currentPage === 0}
										className="px-2 py-1 text-xs bg-white rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
									>
										Previous
									</button>
									<span className="text-xs">
										{currentPage + 1} / {processedData.totalPages}
									</span>
									<button
										onClick={handleNextPage}
										disabled={!processedData.hasMore}
										className="px-2 py-1 text-xs bg-white rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
									>
										Next
									</button>
								</div>
							</div>
						</>
					)}

					{/* JSON Content */}
					<div className="overflow-x-auto max-h-[400px] overflow-y-auto">
						<JsonNode data={processedData?.data} depth={0} maxDepth={maxDepth} />
					</div>

					{/* Truncation warning */}
					{processedData?.truncated && (
						<div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
							<div className="text-xs text-yellow-800 dark:text-yellow-200">
								⚠️ Large object truncated to first {maxItems} properties.
								{processedData.totalItems - maxItems} more properties hidden.
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

/**
 * Recursive component to render JSON tree structure with collapsible nodes
 */
function JsonNode({ data, depth, maxDepth, keyName, isLast = true }: JsonNodeProps) {
	const [isExpanded, setIsExpanded] = useState(depth < 2); // Auto-expand first 2 levels

	// Prevent infinite recursion
	if (depth > maxDepth) {
		return <span className="text-gray-500 dark:text-gray-400 italic">[Max depth reached]</span>;
	}

	// Handle null/undefined
	if (data === null || data === undefined) {
		return <span className="text-gray-500 dark:text-gray-400">{String(data)}</span>;
	}

	// Handle primitives
	if (typeof data !== 'object') {
		const valueClass =
			typeof data === 'string'
				? 'text-green-600 dark:text-green-400'
				: typeof data === 'number'
				? 'text-blue-600 dark:text-blue-400'
				: typeof data === 'boolean'
				? 'text-purple-600 dark:text-purple-400'
				: 'text-gray-600 dark:text-gray-400';

		return (
			<span className={valueClass}>
				{typeof data === 'string' ? `"${data}"` : String(data)}
			</span>
		);
	}

	// Handle arrays
	if (Array.isArray(data)) {
		if (data.length === 0) {
			return <span className="text-gray-500 dark:text-gray-400">[]</span>;
		}

		return (
			<div className="inline-block">
				<button
					onClick={() => setIsExpanded(!isExpanded)}
					className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
				>
					{isExpanded ? (
						<ChevronDown className="w-3 h-3" />
					) : (
						<ChevronRight className="w-3 h-3" />
					)}
					<span className="text-gray-500 dark:text-gray-400">[{data.length}]</span>
				</button>
				{isExpanded && (
					<div className="ml-4 border-l border-gray-200 dark:border-gray-700 pl-2">
						{data.map((item, index) => (
							<div key={index} className="py-0.5">
								<span className="text-gray-500 dark:text-gray-400 mr-2">
									{index}:
								</span>
								<JsonNode
									data={item}
									depth={depth + 1}
									maxDepth={maxDepth}
									isLast={index === data.length - 1}
								/>
							</div>
						))}
					</div>
				)}
			</div>
		);
	}

	// Handle objects
	const entries = Object.entries(data);
	if (entries.length === 0) {
		return <span className="text-gray-500 dark:text-gray-400">{'{}'}</span>;
	}

	return (
		<div className="inline-block">
			<button
				onClick={() => setIsExpanded(!isExpanded)}
				className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
			>
				{isExpanded ? (
					<ChevronDown className="w-3 h-3" />
				) : (
					<ChevronRight className="w-3 h-3" />
				)}
				<span className="text-gray-500 dark:text-gray-400">
					{'{'}...{'}'}
				</span>
			</button>
			{isExpanded && (
				<div className="ml-4 border-l border-gray-200 dark:border-gray-700 pl-2">
					{entries.map(([key, value], index) => (
						<div key={key} className="py-0.5">
							<span className="text-blue-700 dark:text-blue-300 mr-2">"{key}":</span>
							<JsonNode
								data={value}
								depth={depth + 1}
								maxDepth={maxDepth}
								keyName={key}
								isLast={index === entries.length - 1}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
